import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void
          renderButton: (element: HTMLElement, config: Record<string, unknown>) => void
        }
      }
    }
  }
}

export default function LoginPage() {
  const { user, loading, signInWithGoogle, signInWithEmail } = useAuth()
  const navigate = useNavigate()
  const buttonRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [emailLoading, setEmailLoading] = useState(false)
  const [gsiReady, setGsiReady] = useState(false)

  // Load the Google Identity Services script
  useEffect(() => {
    if (window.google?.accounts) {
      setGsiReady(true)
      return
    }

    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.onload = () => setGsiReady(true)
    document.head.appendChild(script)
  }, [])

  // Initialize Google Sign-In once the script is loaded
  useEffect(() => {
    if (!gsiReady || !buttonRef.current || !window.google) return

    window.google.accounts.id.initialize({
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: async (response: { credential: string }) => {
        setError('')
        const result = await signInWithGoogle(response.credential)
        if (result.success) {
          navigate('/dashboard')
        } else {
          setError(result.error || 'Sign in failed')
        }
      },
    })

    window.google.accounts.id.renderButton(buttonRef.current, {
      type: 'standard',
      shape: 'pill',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: 300,
    })
  }, [gsiReady, signInWithGoogle, navigate, loading])

  const handleEmailSignIn = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }

    setEmailLoading(true)
    const result = await signInWithEmail(email.trim())
    setEmailLoading(false)

    if (result.success) {
      navigate('/dashboard')
    } else {
      setError(result.error || 'Sign in failed')
    }
  }

  if (loading) return null
  if (user) return <Navigate to="/dashboard" />

  const watercolorBg = `radial-gradient(ellipse at 20% 20%, hsla(270,30%,95%,0.6) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 80%, hsla(300,30%,95%,0.4) 0%, transparent 50%),
    radial-gradient(ellipse at 50% 50%, hsla(285,20%,98%,1) 0%, hsla(270,10%,99%,1) 100%)`

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#f8f5fc', fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <div style={{
        background: watercolorBg, borderRadius: 24, padding: 48,
        maxWidth: 420, width: '100%', margin: 20,
        boxShadow: '0 4px 30px rgba(100,60,140,0.08)',
        border: '1px solid rgba(180,160,210,0.2)',
        textAlign: 'center',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', margin: '0 auto 20px',
          background: 'linear-gradient(135deg, rgba(124,77,186,0.15), rgba(155,114,207,0.1))',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36,
        }}>🕊</div>

        <h1 style={{ fontFamily: "'Georgia', serif", color: '#2d1b4e', fontSize: 24, margin: '0 0 4px', fontWeight: 400 }}>
          Minister's Report
        </h1>
        <p style={{ color: '#7c6a94', fontSize: 14, margin: '0 0 32px', fontStyle: 'italic' }}>
          Centers for Spiritual Living — 2025 Annual Review
        </p>

        <div style={{ display: 'flex', justifyContent: 'center', minHeight: 44, marginBottom: 16 }}>
          <div ref={buttonRef} />
        </div>

        {!gsiReady && (
          <p style={{ color: '#9a80c0', fontSize: 13, marginTop: -10, marginBottom: 16 }}>Loading Google sign-in...</p>
        )}

        <p style={{ color: '#9a80c0', fontSize: 12, margin: '8px 0 16px' }}>&mdash; or &mdash;</p>

        <form onSubmit={handleEmailSignIn} style={{ marginBottom: 20 }}>
          <input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: '12px 14px',
              borderRadius: 12,
              border: '1px solid rgba(180,160,210,0.5)',
              marginBottom: 10,
              fontSize: 14,
            }}
          />
          <button
            type="submit"
            disabled={emailLoading}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: 999,
              border: 'none',
              background: 'linear-gradient(135deg, #7c4dba, #9b72cf)',
              color: 'white',
              fontWeight: 600,
              cursor: emailLoading ? 'default' : 'pointer',
              opacity: emailLoading ? 0.7 : 1,
            }}
          >
            {emailLoading ? 'Signing in...' : 'Continue with Email'}
          </button>
        </form>

        {error && (
          <p style={{ color: '#c44', fontSize: 13, margin: '12px 0 0' }}>{error}</p>
        )}
      </div>
    </div>
  )
}
